import { useMutationObserver } from '@vueuse/core';
import { type Ref, onMounted, watchEffect } from 'vue';

import { useMissingNameWarning } from './useMissingNameWarning';

const COMBOBOX_INPUT_SELECTOR = '.ant-select-selection-search-input';
const DUPLICATE_VALUE_SELECTOR = [
  '.ant-select-selection-item',
  '.ant-select-selection-overflow-item:not(.ant-select-selection-overflow-item-suffix)',
  '.ant-select > [aria-live]',
].join(', ');

type ComboboxInputAriaType = {
  ariaLabel?: string | null;
  ariaLabelledby?: string | null;
  hasVisibleLabel?: boolean;
  id?: string | null;
  componentName: string;
  isInvalid?: boolean;
  ariaDescribedby?: string | null;
  valueTextId?: string | null;
};

function nameSourceIds(
  input: HTMLInputElement,
  { ariaLabel, ariaLabelledby, hasVisibleLabel }: ComboboxInputAriaType,
) {
  if (!hasVisibleLabel && ariaLabelledby) return ariaLabelledby;
  if (!hasVisibleLabel && ariaLabel) return input.id || null;
  const labelIds = Array.from(input.labels ?? [], (label) => label.id);
  return labelIds.filter(Boolean).join(' ') || null;
}

function setOrRemove(el: Element, attr: string, value?: string | null) {
  if (value) el.setAttribute(attr, value);
  else el.removeAttribute(attr);
}

/**
 * Puts `aria-label` / `aria-labelledby`, `aria-invalid` and `aria-describedby`
 * on the combobox input that Ant Design's Select renders, which is the element
 * that receives focus. Ant forwards these attributes to its wrapper div instead
 * (ant-design-vue 4.2.6), so a select named or marked invalid on the component
 * is still announced as an unnamed, valid combobox.
 *
 * A visible label (`hasVisibleLabel`, from the component's `formLabel`) already
 * names the input through `<label for>`, so it wins: the aria props are not
 * applied and passing both logs a warning.
 *
 * Ant leaves the input's own value empty and renders the selection in sibling
 * elements, so the current value is not announced on focus. When the component
 * passes `valueTextId` (a hidden element holding the selected label(s)), it is
 * appended to `aria-labelledby` after the name source: the visible label, the
 * `ariaLabelledby` ids, or the input itself, which contributes its
 * `aria-label`. Ant's visual selection and its own polite live region (which
 * holds the raw option value whenever the label is markup) are then hidden from
 * assistive technology so the value is read once, as its label. Without a name
 * source the value is left out, as it would otherwise replace the name.
 *
 * Re-applies when Ant replaces the input (for example when `showSearch` toggles)
 * and whenever the name changes. Vue does not patch attributes it did not set,
 * so ordinary re-renders keep the name.
 *
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/combobox/ (2026-10-05)
 * @see https://www.w3.org/TR/accname-1.2/#comp_labelledby (2026-10-12)
 */
export function useComboboxInputAria(
  wrapper: Ref<HTMLElement | null>,
  getAria: () => ComboboxInputAriaType,
) {
  function applyAria() {
    const input = wrapper.value?.querySelector<HTMLInputElement>(
      COMBOBOX_INPUT_SELECTOR,
    );
    if (!input) return;
    const aria = getAria();
    const {
      ariaLabel,
      ariaLabelledby,
      hasVisibleLabel,
      isInvalid,
      ariaDescribedby,
      valueTextId,
    } = aria;
    const nameIds = valueTextId ? nameSourceIds(input, aria) : null;
    const isValueInName = !!nameIds;
    setOrRemove(input, 'aria-label', hasVisibleLabel ? null : ariaLabel);
    setOrRemove(
      input,
      'aria-labelledby',
      isValueInName
        ? `${nameIds} ${valueTextId}`
        : hasVisibleLabel
          ? null
          : ariaLabelledby,
    );
    setOrRemove(input, 'aria-invalid', isInvalid ? 'true' : null);
    setOrRemove(input, 'aria-describedby', ariaDescribedby);
    wrapper.value
      ?.querySelectorAll(DUPLICATE_VALUE_SELECTOR)
      .forEach((duplicate) =>
        setOrRemove(duplicate, 'aria-hidden', isValueInName ? 'true' : null),
      );
  }

  onMounted(() => {
    const { ariaLabel, ariaLabelledby, hasVisibleLabel } = getAria();
    if (hasVisibleLabel && (ariaLabel || ariaLabelledby)) {
      console.warn(
        'ariaLabel/ariaLabelledby are ignored when formLabel is set; the visible label already names the select',
      );
    }
  });
  const { ariaLabel, ariaLabelledby, hasVisibleLabel, id, componentName } =
    getAria();
  useMissingNameWarning({
    isNamed: !!(hasVisibleLabel || ariaLabel || ariaLabelledby || id),
    component: componentName,
    role: 'combobox',
    hint: 'set formLabel, ariaLabel, ariaLabelledby, or id (wired to a visible <label>)',
  });
  useMutationObserver(wrapper, applyAria, { childList: true, subtree: true });
  watchEffect(applyAria, { flush: 'post' });
}
